import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AppModuleLayout } from "@layouts";
import { PageHead, SectionBlockRenderer, ClipLoaderCommon } from "@components";
import { cmsApi } from "@api";
import { CmsPost } from "@interfaces";
import { theme, getSiteMeta, buildArticleJsonLd, buildBreadcrumbJsonLd } from "@utilities";
import { NotFound } from "../NotFound";

type LoadState = "loading" | "ready" | "notfound" | "error";

const formatDate = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

/** Blog post detail (`/blog/:slug`). Handles loading / not-found / error. */
export const BlogPost = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const preview = searchParams.get("preview") === "1" || searchParams.get("preview") === "true";
    const [state, setState] = useState<LoadState>("loading");
    const [post, setPost] = useState<CmsPost | null>(null);

    // Deterministic fallback title on mount so the tab/analytics never show the
    // previous route's title while the post is still loading. PageHead replaces
    // it with the real post title once the fetch resolves.
    useEffect(() => {
        document.title = `Blog | ${theme.title}`;
    }, [slug]);

    useEffect(() => {
        let active = true;
        setState("loading");
        window.scrollTo({ top: 0 });
        (async () => {
            try {
                const result = await cmsApi.getPostBySlug(slug ?? "", { preview });
                if (!active) return;
                if (!result) {
                    setState("notfound");
                    return;
                }
                setPost(result);
                setState("ready");
            } catch (err) {
                if (!active) return;
                console.error("[BlogPost] failed to load", err);
                setState("error");
            }
        })();
        return () => {
            active = false;
        };
    }, [slug, preview]);

    if (state === "notfound") return <NotFound />;

    return (
        <AppModuleLayout>
            <Container>
                {state === "loading" && (
                    <ClipLoaderCommon color={theme.colors.highlight} size={60} margin={120} />
                )}
                {state === "error" && (
                    <div className="postMessage">
                        <h2>Something went wrong</h2>
                        <p>We couldn’t load this post. Please try again shortly.</p>
                    </div>
                )}
                {state === "ready" && post && (
                    <>
                        <PageHead
                            seo={post.seo}
                            ogType="article"
                            jsonLd={(() => {
                                const site = getSiteMeta();
                                const path = `/blog/${post.slug}`;
                                const url = site.siteUrl ? `${site.siteUrl}${path}` : path;
                                return [
                                    buildArticleJsonLd(
                                        {
                                            headline: post.title,
                                            description: post.excerpt || post.seo?.metaDescription,
                                            url,
                                            image: post.coverImage,
                                            authorName: post.author?.name,
                                            datePublished: post.publishedAt,
                                            type: "BlogPosting",
                                        },
                                        site,
                                    ),
                                    buildBreadcrumbJsonLd(
                                        [
                                            { name: "Home", url: site.siteUrl || "/" },
                                            { name: "Blog", url: site.siteUrl ? `${site.siteUrl}/blog` : "/blog" },
                                            { name: post.title, url },
                                        ],
                                        site,
                                    ),
                                ].filter(Boolean);
                            })()}
                        />
                        {preview && post.status !== "published" && (
                            <PreviewBanner>Preview — this post is not published yet.</PreviewBanner>
                        )}
                        <article className="article">
                            <Link className="back" to="/blog">
                                ← Back to Blog
                            </Link>
                            <h1 className="title">{post.title}</h1>
                            <div className="meta">
                                {post.author?.name && <span>{post.author.name}</span>}
                                {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
                                {post.readingMinutes && <span>{post.readingMinutes} min read</span>}
                            </div>
                            {post.coverImage && (
                                <img className="cover" src={post.coverImage} alt={post.title} />
                            )}
                            <SectionBlockRenderer sections={post.sections} />
                            {post.tags && post.tags.length > 0 && (
                                <div className="tags">
                                    {post.tags.map((t) => (
                                        <Link className="tag" key={t} to={`/blog?tag=${encodeURIComponent(t)}`}>
                                            #{t}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </article>
                    </>
                )}
            </Container>
        </AppModuleLayout>
    );
};

const Container = styled.div`
    background-color: #fff;
    width: 100%;
    min-height: 60vh;
    padding: 24px 0 60px;

    .postMessage {
        text-align: center;
        padding: 80px 16px;
    }
    .article {
        max-width: 760px;
        margin: 0 auto;
        padding: 0 16px;
    }
    .back {
        display: inline-block;
        margin-bottom: 16px;
        color: ${theme.colors.highlight};
        text-decoration: none;
        font-weight: 600;
    }
    .title {
        font-size: 34px;
        font-weight: 700;
        margin: 0 0 12px;
    }
    .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        font-size: 13px;
        color: ${theme.colors.gray_2};
        margin-bottom: 20px;
    }
    .cover {
        width: 100%;
        border-radius: 12px;
        margin-bottom: 24px;
        object-fit: cover;
    }
    .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 24px;
    }
    .tag {
        color: ${theme.colors.highlight};
        text-decoration: none;
        font-size: 14px;
    }
`;

const PreviewBanner = styled.div`
    background: #fff4d6;
    color: #7a5b00;
    text-align: center;
    padding: 10px 16px;
    font-weight: 600;
    margin-bottom: 16px;
`;
